<script setup lang="ts">

const props = withDefaults(
  defineProps<{
    label: string;
    format?: string;
    disabled?: boolean;
    rules?: any;
    placeholder?: string;
    maxDate?: Date;
    minDate?: Date;
    errorMessages?: any;
    is24hr?: boolean;
    isTimePicker?: boolean;
    increment?: number;
  }>(),
  {
    increment: 5,
  }
);

const modelValue = defineModel<string>({
  default: '01:05'
});

// Internal state for time components
const internalTime = ref({
  hours: 1,
  minutes: 5,
  period: 'AM' as 'AM' | 'PM'
});

// Parse time string into components
const parseTimeString = (timeStr: string) => {
  if (!timeStr) return;
  
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  
  if (props.is24hr) {
    internalTime.value.hours = hours;
    internalTime.value.minutes = minutes;
    internalTime.value.period = hours < 12 ? 'AM' : 'PM';
  } else {
    internalTime.value.hours = hours;
    internalTime.value.minutes = minutes;
    internalTime.value.period = period as 'AM' | 'PM';
  }
};

// Watch for external model changes
watch(modelValue, (newValue: string) => {
  if (newValue) {
    parseTimeString(newValue);
  }
}, { immediate: true });

// Convert 12-hour to 24-hour format
const convertTo24Hour = (hours: number, period: 'AM' | 'PM'): number => {
  if (period === 'PM' && hours !== 12) {
    return hours + 12;
  }
  if (period === 'AM' && hours === 12) {
    return 0;
  }
  return hours;
};

// Update model value when internal time changes
const updateModelValue = () => {
  const { hours, minutes, period } = internalTime.value;
  
  if (props.is24hr) {
    const hour24 = convertTo24Hour(hours, period);
    const formattedTime = `${hour24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    modelValue.value = formattedTime;
  } else {
    const formattedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    modelValue.value = formattedTime;
  }
};

// Watch internal time changes
watch(internalTime, updateModelValue, { deep: true });

// Hour input handlers
const updateHours = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value) || 0;
  const maxHour = props.is24hr ? 23 : 12;
  const minHour = props.is24hr ? 0 : 1;
  
  if (value >= minHour && value <= maxHour) {
    internalTime.value.hours = value;
  } else {
    // Reset to previous valid value
    target.value = internalTime.value.hours.toString().padStart(2, '0');
  }
};

// Minute input handlers
const updateMinutes = (event: Event) => {
  event.preventDefault();
  event.stopPropagation();
  
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value) || 0;
  
  if (value >= 0 && value <= 59) {
    internalTime.value.minutes = value;
  } else {
    // Reset to previous valid value
    target.value = internalTime.value.minutes.toString().padStart(2, '0');
  }
};

// AM/PM toggle
const togglePeriod = () => {
  internalTime.value.period = internalTime.value.period === 'AM' ? 'PM' : 'AM';
};

// Minute dial functionality
const minuteOptions = computed(() => {
  const options = [];
  for (let i = 0; i < 60; i += props.increment) {
    options.push({
      value: i,
      label: i.toString().padStart(2, '0'),
      angle: (i / 60) * 360
    });
  }
  return options;
});

const selectMinute = (minute: number) => {
  internalTime.value.minutes = minute;
};

// Calculate position for minute hand
const minuteHandAngle = computed(() => {
  return (internalTime.value.minutes / 60) * 360;
});

// Legacy dropdown times for non-time-picker mode
const times = computed(() => {
  const options = [];
  // Start from 12:00 AM (0) to 11:59 PM (23:59)
  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += props.increment) {
      const hours = i < 10 ? `0${i}` : `${i}`;
      const minutes = j < 10 ? `0${j}` : `${j}`;
      const timeFormat = props.is24hr
        ? `${hours}:${minutes}`
        : `${((i + 11) % 12) + 1}:${minutes} ${i < 12 ? 'AM' : 'PM'}`;
      options.push(timeFormat);
    }
  }
  return options;
});
</script>

<template>
  <v-select
    v-model="modelValue"
    hide-details="auto"
    variant="solo-filled"
    flat
    :items="times"
    :label="label"
    :disabled="props.disabled"
    :rules="props.rules"
    :placeholder="props.placeholder"
    :error-messages="props.errorMessages"
  />
</template>
